import React, { useEffect, useRef, useState } from 'react';

/**
 * Registratore vocale stile WhatsApp.
 *
 * Stati visivi:
 *  - IDLE: bottone microfono. Click → chiede permesso e inizia a registrare.
 *  - RECORDING: bottone rosso pulsante + timer + bottone stop + bottone annulla.
 *
 * Quando si stoppa (manualmente o per scadenza) chiama onRecorded(file).
 *
 * Props:
 *  - onRecorded(file: File)        callback con File audio/webm
 *  - maxSeconds (default 120)      durata massima registrazione
 *  - disabled (bool)               disabilita il bottone
 */
const VoiceRecorder = ({ onRecorded, maxSeconds = 120, disabled = false }) => {
    const [state, setState] = useState("IDLE"); // IDLE | RECORDING
    const [seconds, setSeconds] = useState(0);
    const [error, setError] = useState("");

    const mediaRecorderRef = useRef(null);
    const streamRef = useRef(null);
    const chunksRef = useRef([]);
    const tickRef = useRef(null);
    const cancelledRef = useRef(false);

    useEffect(() => {
        return () => {
            // cleanup su unmount
            stopTimer();
            stopStream();
        };
    }, []);

    const startTimer = () => {
        setSeconds(0);
        tickRef.current = setInterval(() => {
            setSeconds((prev) => {
                const next = prev + 1;
                if (next >= maxSeconds) {
                    // raggiunto il limite → stop automatico
                    stopRecording(false);
                }
                return next;
            });
        }, 1000);
    };

    const stopTimer = () => {
        if (tickRef.current) {
            clearInterval(tickRef.current);
            tickRef.current = null;
        }
    };

    const stopStream = () => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((t) => t.stop());
            streamRef.current = null;
        }
    };

    const pickMimeType = () => {
        const candidates = [
            'audio/webm;codecs=opus',
            'audio/webm',
            'audio/mp4',
            'audio/ogg;codecs=opus',
            'audio/ogg'
        ];
        for (const m of candidates) {
            if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(m)) {
                return m;
            }
        }
        return '';
    };

    const startRecording = async () => {
        setError("");
        cancelledRef.current = false;

        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            setError("Il browser non supporta la registrazione audio.");
            return;
        }

        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;

            const mimeType = pickMimeType();
            const options = mimeType ? { mimeType } : {};
            const recorder = new MediaRecorder(stream, options);
            mediaRecorderRef.current = recorder;
            chunksRef.current = [];

            recorder.ondataavailable = (e) => {
                if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
            };

            recorder.onstop = () => {
                stopTimer();
                stopStream();

                if (cancelledRef.current) {
                    chunksRef.current = [];
                    setState("IDLE");
                    return;
                }

                const usedMime = (recorder.mimeType || mimeType || 'audio/webm').split(';')[0];
                const ext = usedMime.split('/')[1] || 'webm';
                const blob = new Blob(chunksRef.current, { type: usedMime });

                if (blob.size === 0) {
                    setError("Registrazione vuota.");
                    setState("IDLE");
                    return;
                }

                const ts = Date.now();
                const file = new File([blob], "vocale_" + ts + "." + ext, { type: usedMime });

                chunksRef.current = [];
                setState("IDLE");

                if (typeof onRecorded === 'function') {
                    onRecorded(file);
                }
            };

            recorder.start();
            setState("RECORDING");
            startTimer();
        } catch (e) {
            console.error("[voice-recorder] getUserMedia error:", e);
            if (e.name === "NotAllowedError" || e.name === "PermissionDeniedError") {
                setError("Permesso microfono negato.");
            } else if (e.name === "NotFoundError") {
                setError("Nessun microfono trovato.");
            } else {
                setError("Impossibile avviare la registrazione.");
            }
        }
    };

    const stopRecording = (cancel = false) => {
        cancelledRef.current = !!cancel;
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
            try {
                mediaRecorderRef.current.stop();
            } catch (e) {
                console.error("[voice-recorder] stop error:", e);
                stopTimer();
                stopStream();
                setState("IDLE");
            }
        } else {
            stopTimer();
            stopStream();
            setState("IDLE");
        }
    };

    const formatTime = (s) => {
        const m = Math.floor(s / 60);
        const sec = s % 60;
        return String(m).padStart(2, '0') + ":" + String(sec).padStart(2, '0');
    };

    if (state === "RECORDING") {
        return (
            <span className="d-inline-flex align-items-center gap-2 me-1" style={{ verticalAlign: "middle" }}>
                <button
                    type="button"
                    className="btn btn-ghost-danger btn-icon waves-effect shadow-none"
                    onClick={() => stopRecording(true)}
                    title="Annulla registrazione"
                >
                    <i className="mdi mdi-close fs-20"></i>
                </button>
                <span
                    className="d-inline-flex align-items-center px-2 py-1 rounded"
                    style={{ background: "#ffe5e5", color: "#c00", fontWeight: 600, fontSize: 14 }}
                >
                    <span
                        style={{
                            display: "inline-block",
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            background: "#e60000",
                            marginRight: 6,
                            animation: "voiceRecPulse 1s infinite"
                        }}
                    />
                    {formatTime(seconds)} / {formatTime(maxSeconds)}
                </span>
                <button
                    type="button"
                    className="btn btn-success btn-icon waves-effect shadow-none"
                    onClick={() => stopRecording(false)}
                    title="Invia vocale"
                >
                    <i className="mdi mdi-send fs-20"></i>
                </button>
                <style>
                    {`@keyframes voiceRecPulse { 0%,100% { opacity: 1; } 50% { opacity: 0.3; } }`}
                </style>
            </span>
        );
    }

    return (
        <span className="d-inline-flex align-items-center me-1" style={{ verticalAlign: "middle" }}>
            <button
                type="button"
                className="btn btn-ghost-secondary btn-icon waves-effect shadow-none"
                onClick={startRecording}
                disabled={disabled}
                title={error || "Registra vocale"}
            >
                <i className="mdi mdi-microphone fs-20"></i>
            </button>
            {error ? <small className="text-danger ms-1">{error}</small> : null}
        </span>
    );
};

export default VoiceRecorder;
